import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AssemblyQuoteSheet from "@/components/AssemblyQuoteSheet";

export default function AssemblyPreview() {
  const { id } = useParams();
  const [asm, setAsm] = useState(null);
  const [componentNpds, setComponentNpds] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, c, s, u] = await Promise.all([
          api.get(`/assemblies/${id}`),
          api.get("/customers"),
          api.get("/settings/public"),
          api.get("/units"),
        ]);
        setAsm(a.data);
        setCustomers(c.data || []);
        setSettings(s.data || {});
        setUnits(u.data || []);

        // Fetch each component — an NPD part, or a NESTED assembly (asm_ id).
        const ids = (a.data.components || []).map((cm) => cm.npd_id).filter(Boolean);
        if (ids.length > 0) {
          const npds = await Promise.all(ids.map((nid) => {
            if (String(nid).startsWith("asm_")) {
              return api.get(`/assemblies/${nid}`).then((r) => ({
                npd_id: nid, _is_assembly: true,
                grand_total: r.data.grand_total,
                development_cost_total: r.data.development_cost_total,
                npd_number: r.data.assembly_number,
                part_name: r.data.assembly_name, part_number: "",
              })).catch(() => null);
            }
            return api.get(`/npd/${nid}`).then((r) => r.data).catch(() => null);
          }));
          setComponentNpds(npds.filter(Boolean));
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Failed to load assembly";
        setLoadError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (loadError) return (
    <div className="p-10 text-center">
      <div className="text-red-600 font-medium mb-2">Could not load preview</div>
      <div className="text-sm text-slate-500">{loadError}</div>
      <Link to={`/assemblies/${id}`} className="inline-block mt-4 text-sm text-orange-600 hover:text-orange-700">
        ← Back to assembly
      </Link>
    </div>
  );
  if (!asm) return <div className="p-10 text-center text-slate-400">Assembly not found</div>;
  const customerName = customers.find((c) => c.customer_id === asm.customer_id)?.name || "—";
  const unit = units.find((u) => u.unit_id === asm.unit_id) || null;

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between print:hidden">
        <Link to={`/assemblies/${id}`} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5">
          <ArrowLeft size={16} /> Back to Assembly
        </Link>
        <div className="text-sm text-slate-500">Preview · {asm.assembly_number}</div>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
          data-testid="print-btn"
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white shadow-md p-6 my-6 print:shadow-none print:my-0 print:p-0">
        <AssemblyQuoteSheet asm={asm} componentNpds={componentNpds} customerName={customerName} settings={settings} unit={unit} />
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .quote-sheet { font-size: 11px; }
        }
      `}</style>
    </div>
  );
}
